import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { Navigate, useOutlet } from "react-router-dom";
import Sidebar from "components/Sidebar";
import Navbar from "components/Navbar";
import { useStateContext } from "contexts/ContextProvider";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "../../contexts/AuthContext";
import { useConnectivity } from "hooks/useConnectivity";
import { useVersionCheck } from "hooks/useVersionCheck";
import { useEdgeSwipeToOpen } from "hooks/useEdgeSwipeToOpen";
import UpdateNotification from "components/UpdateNotification";
import { NAVBAR_HEIGHT } from "constants/layout";
import api from "services/api";

const Layout = () => {
  const { userLoggedIn, serverUnreachable } = useAuth();
  const { currentUser } = useAuth();
  const { isReconnecting } = useConnectivity();
  const { socket } = useWebSocket();

  const outlet = useOutlet();
  const { updateAvailable, refreshPage } = useVersionCheck();

  const isNonMobile = useMediaQuery("(min-width: 600px)");
  const [isClockedIn, setIsClockedIn] = useState(null);
  const isTimesheetsHiddenForCurrentUser = Number(currentUser?.id) === 1;

  // Use the context for sidebar state management
  const {
    activeMenu,
    setActiveMenu,
    screenSize,
    setScreenSize,
    isCollapsed,
    setIsCollapsed,
    overlayName,
  } = useStateContext();

  // Define drawer widths
  const drawerWidth = 288;
  const collapsedDrawerWidth = Math.floor(drawerWidth * 0.25); // 25% of full width

  // Calculate current drawer width based on collapsed state
  const currentDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;

  const fetchTimesheetStatus = useCallback(async () => {
    if (!currentUser?.id) {
      setIsClockedIn(null);
      return;
    }

    try {
      const response = await api.get("/timesheets/me/status");
      setIsClockedIn(Boolean(response?.data?.isClockedIn));
    } catch (error) {
      // Keep previous bar state on transient failures.
    }
  }, [currentUser?.id]);

  useEffect(() => {
    fetchTimesheetStatus();
    // Note: periodic timesheet polling is handled by Navbar globally,
    // so we only need the initial fetch here to avoid duplicate requests.
  }, [currentUser?.id, fetchTimesheetStatus]);

  useEffect(() => {
    if (!socket || !currentUser?.id) {
      return undefined;
    }

    const handleTimesheetUpdated = ({ userId }) => {
      if (Number(userId) !== Number(currentUser.id)) {
        return;
      }

      fetchTimesheetStatus();
    };

    socket.on("timesheet:updated", handleTimesheetUpdated);
    return () => {
      socket.off("timesheet:updated", handleTimesheetUpdated);
    };
  }, [socket, currentUser?.id, fetchTimesheetStatus]);

  const bottomBarBackgroundColor = useMemo(() => {
    if (!isTimesheetsHiddenForCurrentUser && isClockedIn === false) {
      return (theme) => theme.palette.error.main;
    }

    return (theme) => theme.palette.background.default;
  }, [isClockedIn, isTimesheetsHiddenForCurrentUser]);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [setScreenSize]);

  useEffect(() => {
    if (screenSize <= 900) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  }, [screenSize, setActiveMenu]);

  // Edge-swipe navigation on mobile (≤900px): a rightward drag from the left
  // screen edge opens the sidebar; a leftward drag closes it when open.
  // Mirrors the Navbar hamburger's mobile behavior exactly — opening always
  // resets to the full (un-collapsed) drawer. Desktop (≥900px) never attaches
  // the gesture listeners (see hooks/useEdgeSwipeToOpen.js for the model).
  const handleEdgeSwipeOpen = useCallback(() => {
    setActiveMenu(true);
    setIsCollapsed(false);
  }, [setActiveMenu, setIsCollapsed]);

  const handleEdgeSwipeClose = useCallback(() => {
    setActiveMenu(false);
  }, [setActiveMenu]);

  // While an app overlay (Overlays.js) is open, the gesture is disabled —
  // overlayName covers the custom overlay system; the hook itself also
  // ignores touches while a visible MUI modal (picker/dialog/menu) is up.
  useEdgeSwipeToOpen({
    enabled:
      userLoggedIn &&
      !overlayName &&
      typeof screenSize === "number" &&
      screenSize <= 900,
    isOpen: activeMenu,
    onOpen: handleEdgeSwipeOpen,
    onClose: handleEdgeSwipeClose,
  });

  // Cold-load outage: the session has not hydrated yet (serverUnreachable)
  // or a reconnect episode is in progress → render nothing instead of
  // bouncing to login. The app-wide overlay (mounted once by
  // `NetworkErrorListener`) owns the screen. A LOGGED-IN outage never hits
  // this branch: the full layout below stays mounted so unsaved work
  // survives underneath the overlay.
  if (!userLoggedIn && (isReconnecting || serverUnreachable)) {
    return null;
  }

  if (!userLoggedIn) return <Navigate to="/" replace />;
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Sidebar */}
      <Sidebar
        user={currentUser}
        isNonMobile={isNonMobile}
        drawerWidth={drawerWidth}
      />

      {/* Main Content Container */}
      <Box
        sx={{
          flexGrow: 1,
          width:
            isNonMobile && activeMenu
              ? `calc(100% - ${currentDrawerWidth}px)`
              : "100%",

          ml: isNonMobile && activeMenu ? `${currentDrawerWidth}px` : 0,
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          transition: (theme) =>
            theme.transitions.create(["width", "margin-left"], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
          bgcolor: "background.default",
        }}
      >
        {/* Navbar - Fixed at top */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            minHeight: NAVBAR_HEIGHT,
            bgcolor: "background.default",
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Navbar user={currentUser} />
        </Box>

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
            }}
          >
            <UpdateNotification
              open={updateAvailable}
              onRefresh={refreshPage}
            />
            <Box>{outlet}</Box>
          </Box>

          {/* Bottom bar */}
          <Box
            sx={{
              height: "20px",
              minHeight: "20px",
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: bottomBarBackgroundColor,
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!isTimesheetsHiddenForCurrentUser && isClockedIn === false && (
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  lineHeight: 1,
                  color: (theme) => theme.palette.error.contrastText,
                  textTransform: "none",
                }}
              >
                Not Clocked in
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
